// SimpleFIN credential + state storage.
//
// The SimpleFIN access_url is the sensitive piece — it carries HTTP
// Basic credentials in its userinfo segment. Read it and you can pull
// every account that user's SimpleFIN bridge subscription covers.
// Before C19 it sat plaintext alongside the other item metadata in
// `pf:simplefin:items`. Now it lives in the encrypted vault
// (vault.ts), keyed by item id, while the non-sensitive metadata
// (org name, last_sync, account overrides) stays plaintext so the UI
// can render "Connected to X" pre-unlock.
//
// Storage shape:
//   pf:simplefin:items                  plaintext SimpleFinItem[] WITHOUT access_url
//   pf:simplefin:settings               plaintext { pull_window_days, auto_pull_hours }
//   pf:vault:simplefin:access-urls      encrypted { [itemId]: access_url }
//
// Legacy keys auto-migrated on first read:
//   pf:simplefin:access-url, pf:simplefin:last-sync, pf:simplefin:org-name
//     (pre-multi-org single-URL shape — flattened into a one-item array)
//   pf:simplefin:items entries with plaintext `access_url` field
//     (pre-C19 multi-org shape — access_urls moved into the vault, field
//     stripped from the plaintext array on the next save)
//
// Read semantics:
//   - Vault locked: getItems() returns items WITHOUT access_url. UI
//     metadata works (org_name, last_sync, accounts). Network calls
//     (fetchAccounts) will fail with a clear "vault locked" error if
//     they reach this path; callers should ensureUnlocked() first.
//   - Vault unlocked: access_url is populated on every item from the
//     encrypted map. Legacy plaintext access_urls (if present) get
//     migrated into the vault as a side effect.

import { get, set, del } from "idb-keyval";
import * as vault from "../vault/vault.js";

const KEY_ITEMS = "pf:simplefin:items";
const KEY_SETTINGS = "pf:simplefin:settings";
const VAULT_KEY_URLS = "simplefin:access-urls";

// Legacy keys — read once during migration, then deleted.
const LEGACY_KEY_URL = "pf:simplefin:access-url";
const LEGACY_KEY_LAST_SYNC = "pf:simplefin:last-sync";
const LEGACY_KEY_ORG_NAME = "pf:simplefin:org-name";

export interface SimpleFinAccountMeta {
  /** SimpleFIN account.id — stable identifier per bank account. */
  id: string;
  /** Last-seen account.name from the SimpleFIN response. */
  name: string;
  /** Last-seen currency (ISO 4217). */
  currency: string;
  /** User override: replaces the SimpleFIN name in the dashboard. */
  display_name?: string;
  /** User override: when true, skip this account during pulls so its
   *  transactions never enter the dashboard. */
  hidden?: boolean;
  /** User override: forces account_kind on the emitted transactions.
   *  When unset, the loader defaults to "debit". */
  account_kind?: "debit" | "liability";
}

export interface SimpleFinItem {
  /** Locally-generated stable id. Used as the React-key equivalent in the
   *  manage modal and as the suffix on the synthetic pull filename. */
  id: string;
  /** Permanent SimpleFIN access URL (contains HTTP Basic creds in userinfo).
   *  Optional because the vault must be unlocked to populate it — when
   *  locked, getItems() returns items without this field set. Callers
   *  that need it (fetchAccounts) should ensureUnlocked() first. */
  access_url?: string;
  /** Display org name (from accounts[0].org.name on the most recent pull). */
  org_name: string;
  /** ISO timestamp of the most recent successful /accounts pull, or null
   *  if the item was added but never pulled yet. */
  last_sync: string | null;
  /** Known accounts under this access URL. Populated/refreshed after each
   *  pull. Persists across pulls so user overrides survive. */
  accounts: SimpleFinAccountMeta[];
}

// On-disk plaintext shape (intentionally missing access_url so we don't
// accidentally serialize the secret if the in-memory item still has it).
interface SimpleFinItemPlain {
  id: string;
  org_name: string;
  last_sync: string | null;
  accounts: SimpleFinAccountMeta[];
}

export interface SimpleFinSettings {
  /** Default pull window in days. Power users reconciling old data can
   *  raise this; default 90 matches the desktop. */
  pull_window_days: number;
  /** Background auto-pull threshold. If any item's last_sync is older
   *  than this many hours, fire a pull on app open. 0 = disabled. */
  auto_pull_hours: number;
}

const DEFAULT_SETTINGS: SimpleFinSettings = {
  pull_window_days: 90,
  auto_pull_hours: 6,
};

// ── settings ───────────────────────────────────────────────────────

export async function getSettings(): Promise<SimpleFinSettings> {
  const stored = await get<Partial<SimpleFinSettings> | undefined>(KEY_SETTINGS);
  return { ...DEFAULT_SETTINGS, ...(stored ?? {}) };
}

export async function saveSettings(patch: Partial<SimpleFinSettings>): Promise<void> {
  const next = { ...(await getSettings()), ...patch };
  await set(KEY_SETTINGS, next);
}

// ── access-url vault helpers ──────────────────────────────────────

type AccessUrlMap = Record<string, string>;

async function loadAccessUrlMap(): Promise<AccessUrlMap> {
  if (!vault.isUnlocked()) return {};
  return (await vault.getValue<AccessUrlMap>(VAULT_KEY_URLS)) ?? {};
}

async function saveAccessUrlMap(map: AccessUrlMap): Promise<void> {
  await vault.setValue<AccessUrlMap>(VAULT_KEY_URLS, map);
}

/** Read the access_url for one item from the vault. Requires unlock.
 *  Returns null if the vault is locked or no URL exists for the id. */
export async function getAccessUrl(itemId: string): Promise<string | null> {
  if (!vault.isUnlocked()) return null;
  const map = await loadAccessUrlMap();
  return map[itemId] ?? null;
}

// ── items ──────────────────────────────────────────────────────────

/** Read the current list of connected SimpleFIN items. Migrates both
 *  legacy shapes on first read:
 *    - pre-multi-org single-URL keys (access-url/last-sync/org-name)
 *    - pre-C19 plaintext access_url fields inside the items array
 *  Both migrations require the vault to be unlocked to land
 *  access_urls in the encrypted store; with a locked vault, getItems
 *  still returns items but their access_url is undefined and any
 *  legacy plaintext access_urls are left in place for the next
 *  unlocked call to migrate. */
export async function getItems(): Promise<SimpleFinItem[]> {
  const stored = await get<Array<SimpleFinItem | SimpleFinItemPlain> | undefined>(KEY_ITEMS);
  const unlocked = vault.isUnlocked();
  const urlMap = unlocked ? await loadAccessUrlMap() : {};
  let mapDirty = false;
  let itemsDirty = false;
  let result: SimpleFinItem[];

  if (Array.isArray(stored)) {
    result = [];
    for (const raw of stored) {
      const item: SimpleFinItem = {
        id: raw.id,
        org_name: raw.org_name ?? "",
        last_sync: raw.last_sync ?? null,
        accounts: raw.accounts ?? [],
      };
      // Pre-C19 records carry access_url in the plaintext object.
      const legacyUrl = (raw as { access_url?: string }).access_url;
      if (legacyUrl) {
        itemsDirty = true; // we'll re-save without it
        if (unlocked) {
          urlMap[item.id] = legacyUrl;
          mapDirty = true;
          item.access_url = legacyUrl;
        } else {
          // Can't migrate yet — keep access_url on the in-memory item
          // so callers awaiting the unlock get a workable URL, but
          // DON'T re-save the plaintext array (which would strip it).
          item.access_url = legacyUrl;
          itemsDirty = false;
        }
      } else if (unlocked && urlMap[item.id]) {
        item.access_url = urlMap[item.id];
      }
      result.push(item);
    }
  } else {
    // No multi-org list yet — try the original single-URL migration.
    const legacyUrl = await get<string | undefined>(LEGACY_KEY_URL);
    if (!legacyUrl) return [];
    const legacyLastSync = await get<string | undefined>(LEGACY_KEY_LAST_SYNC);
    const legacyOrg = await get<string | undefined>(LEGACY_KEY_ORG_NAME);
    const id = newItemId();
    const item: SimpleFinItem = {
      id,
      access_url: legacyUrl, // keep in-memory so callers can use it now
      org_name: legacyOrg ?? "",
      last_sync: legacyLastSync ?? null,
      accounts: [],
    };
    result = [item];
    if (unlocked) {
      urlMap[id] = legacyUrl;
      mapDirty = true;
      itemsDirty = true; // first save is the encrypted-shape save
    } else {
      // Without vault unlock we can't write to vault yet. Save the
      // legacy access_url alongside the new items array; the next
      // unlocked call will migrate it. Drop the original single-URL
      // legacy keys either way (they've been read into the array).
      const fallback: SimpleFinItem[] = [item];
      await set(KEY_ITEMS, fallback);
    }
    await Promise.all([
      del(LEGACY_KEY_URL),
      del(LEGACY_KEY_LAST_SYNC),
      del(LEGACY_KEY_ORG_NAME),
    ]);
  }

  // Persist migrations (encrypted map + plaintext items without urls).
  if (mapDirty) await saveAccessUrlMap(urlMap);
  if (itemsDirty) await set(KEY_ITEMS, result.map(toPlain));

  return result;
}

function toPlain(item: SimpleFinItem): SimpleFinItemPlain {
  return {
    id: item.id,
    org_name: item.org_name,
    last_sync: item.last_sync,
    accounts: item.accounts,
  };
}

export async function saveItems(items: SimpleFinItem[]): Promise<void> {
  // Always strip access_url before writing the plaintext array. The
  // url goes into the encrypted vault map below.
  await set(KEY_ITEMS, items.map(toPlain));
  if (vault.isUnlocked()) {
    const map = await loadAccessUrlMap();
    let changed = false;
    for (const it of items) {
      if (it.access_url && map[it.id] !== it.access_url) {
        map[it.id] = it.access_url;
        changed = true;
      }
    }
    // Drop urls for items that aren't in the new array (a save that
    // removes an item should also evict its url).
    const liveIds = new Set(items.map((i) => i.id));
    for (const id of Object.keys(map)) {
      if (!liveIds.has(id)) {
        delete map[id];
        changed = true;
      }
    }
    if (changed) await saveAccessUrlMap(map);
  }
}

export async function getItemById(id: string): Promise<SimpleFinItem | null> {
  const items = await getItems();
  return items.find((it) => it.id === id) ?? null;
}

/** Append a new item and persist. Requires an unlocked vault — we
 *  refuse to add a credential we can't encrypt. */
export async function addItem(
  partial: Omit<SimpleFinItem, "id"> & { access_url: string },
): Promise<SimpleFinItem> {
  if (!vault.isUnlocked()) {
    throw new Error("Vault is locked. Unlock the vault before connecting a SimpleFIN bank.");
  }
  const items = await getItems();
  const item: SimpleFinItem = { id: newItemId(), ...partial };
  items.push(item);
  await saveItems(items); // splits + encrypts access_url
  return item;
}

/** Patch fields on one item by id. No-op if id not found. When the
 *  patch includes access_url, the vault must be unlocked or the call
 *  is rejected. */
export async function updateItem(
  id: string,
  patch: Partial<Omit<SimpleFinItem, "id">>,
): Promise<void> {
  if (patch.access_url && !vault.isUnlocked()) {
    throw new Error("Vault is locked. Unlock the vault before updating credentials.");
  }
  const items = await getItems();
  const idx = items.findIndex((it) => it.id === id);
  if (idx < 0) return;
  items[idx] = { ...items[idx], ...patch };
  await saveItems(items);
}

export async function removeItem(id: string): Promise<void> {
  const items = await getItems();
  const next = items.filter((it) => it.id !== id);
  if (next.length === items.length) return;
  await saveItems(next); // also evicts the vault entry for `id`
}

/** Drop every SimpleFIN-related key (items + settings + legacy + vault).
 *  Does NOT delete already-imported transactions from the user's
 *  data folder — those live with their CSV/PDF siblings. */
export async function clearAll(): Promise<void> {
  await Promise.all([
    del(KEY_ITEMS),
    del(KEY_SETTINGS),
    del(LEGACY_KEY_URL),
    del(LEGACY_KEY_LAST_SYNC),
    del(LEGACY_KEY_ORG_NAME),
  ]);
  // Wipe the vault entry too. deleteValue is safe regardless of lock
  // state.
  await vault.deleteValue(VAULT_KEY_URLS);
}

function newItemId(): string {
  // crypto.randomUUID is available in every Chromium release we support
  // (matches the File System Access API gating in folder.ts). Fall back
  // to a timestamp+random combo on the off chance some test harness or
  // mobile shell lacks it.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `sfin-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ── per-pull doc transform (apply user account overrides) ──────────

import type { SimpleFinResponse, SimpleFinAccount } from "./simplefin_client.js";

/** Apply this item's per-account overrides to a freshly-fetched
 *  /accounts response. Returns a NEW response object (does not mutate
 *  the input).
 *
 *  - Drops accounts marked hidden so they never enter the dashboard.
 *  - Replaces acct.name with the user-chosen display_name when set.
 *  - Injects `_pf_account_kind` so loadSimplefinJson can honor the
 *    user's debit/liability override per account. */
export function applyAccountOverrides(
  doc: SimpleFinResponse,
  item: SimpleFinItem,
): SimpleFinResponse {
  const byId = new Map<string, SimpleFinAccountMeta>();
  for (const meta of item.accounts) byId.set(meta.id, meta);

  const accounts: SimpleFinAccount[] = [];
  for (const a of doc.accounts) {
    const meta = byId.get(a.id);
    if (meta?.hidden) continue;
    const transformed: SimpleFinAccount = { ...a };
    if (meta?.display_name && meta.display_name.trim()) {
      transformed.name = meta.display_name.trim();
    }
    if (meta?.account_kind) {
      // Non-standard SimpleFIN field; loadSimplefinJson reads it. Other
      // consumers (the desktop loader, third-party tools) ignore extra
      // fields, so this transformation is safe to round-trip.
      (transformed as SimpleFinAccount & { _pf_account_kind?: "debit" | "liability" })
        ._pf_account_kind = meta.account_kind;
    }
    accounts.push(transformed);
  }
  return { ...doc, accounts };
}

/** The label the SimpleFIN loader would emit for this account on the
 *  next pull — mirrors `simplefinAccountLabel` in loaders/simplefin_json.ts.
 *  Used by main.ts to filter already-imported rows whose `tx.account`
 *  matches a now-hidden account.
 *
 *  Limitation: if the bank renames an account upstream AND the user
 *  hides it AFTER the rename, transactions imported under the OLD label
 *  won't match. Acceptable for the common case; user can also flip
 *  `tx.deleted` on those rows for retroactive cleanup. */
export function effectiveAccountLabel(meta: SimpleFinAccountMeta): string {
  const name = (meta.display_name?.trim() || meta.name?.trim() || "Account");
  const currency = (meta.currency ?? "").trim();
  if (currency && !name.toUpperCase().includes(currency.toUpperCase())) {
    return `${name} (${currency})`;
  }
  return name;
}

/** Build the set of `tx.account` labels that should disappear from the
 *  dashboard because their account is currently flagged hidden. The
 *  caller passes this through to `txMatches` so the filter strips
 *  matching rows at render time (not just on the next pull). */
export function hiddenAccountLabels(items: SimpleFinItem[]): Set<string> {
  const labels = new Set<string>();
  for (const it of items) {
    for (const a of it.accounts) {
      if (a.hidden) labels.add(effectiveAccountLabel(a));
    }
  }
  return labels;
}

/** Refresh the cached account list for an item from a fresh /accounts
 *  response. Preserves existing user overrides on accounts we've seen
 *  before; appends new accounts with no overrides; keeps overrides for
 *  accounts that disappeared from the response (so a temporarily-
 *  unreachable bank doesn't lose its display_name when it returns). */
export function mergeAccountList(
  item: SimpleFinItem,
  doc: SimpleFinResponse,
): SimpleFinAccountMeta[] {
  const existing = new Map<string, SimpleFinAccountMeta>();
  for (const m of item.accounts) existing.set(m.id, m);

  const merged: SimpleFinAccountMeta[] = [];
  const seenIds = new Set<string>();
  for (const a of doc.accounts) {
    seenIds.add(a.id);
    const prev = existing.get(a.id);
    merged.push({
      id: a.id,
      name: a.name ?? prev?.name ?? "",
      currency: a.currency ?? prev?.currency ?? "",
      display_name: prev?.display_name,
      hidden: prev?.hidden,
      account_kind: prev?.account_kind,
    });
  }
  // Preserve accounts that didn't appear in this response (e.g. SimpleFIN
  // returned an empty 'accounts' for a transient error). User overrides
  // shouldn't vanish just because one pull was incomplete.
  for (const m of item.accounts) {
    if (!seenIds.has(m.id)) merged.push(m);
  }
  return merged;
}
